import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * RouteNormalizer component
 * - Forces lowercase URLs
 * - Removes trailing slashes (except root)
 * - Logs redirects for analytics
 */
export const RouteNormalizer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;
    const searchParams = location.search;
    const hash = location.hash;
    
    // Normalize: lowercase and remove trailing slash (except for root "/")
    let normalizedPath = currentPath.toLowerCase();
    if (normalizedPath !== "/" && normalizedPath.endsWith("/")) {
      normalizedPath = normalizedPath.slice(0, -1);
    }

    // If path needs normalization, redirect
    if (currentPath !== normalizedPath) {
      // Log the redirect for analytics
      logRedirect(currentPath, normalizedPath, "normalization");
      
      // Replace the URL (not push, to avoid back button issues)
      navigate(normalizedPath + searchParams + hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

/**
 * Log redirect events to database for analytics
 */
async function logRedirect(
  originalUrl: string, 
  destinationUrl: string, 
  redirectType: string
) {
  try {
    await supabase.from("analytics_events").insert({
      event_name: "route_redirect",
      page_path: originalUrl,
      event_properties: {
        original_url: originalUrl,
        destination_url: destinationUrl,
        redirect_type: redirectType,
        referrer: document.referrer || null,
        timestamp: new Date().toISOString()
      }
    });
  } catch (error) {
    // Silent fail - don't break navigation for analytics
    console.debug("Analytics log failed:", error);
  }
}

export default RouteNormalizer;
